import { useMemo, useState } from 'react';
import clsx from 'clsx';
import type { FathomIngest, FathomSyncStatus } from '../../api/organizer';
import { callCountLabel } from './taskUtils';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Input';
import { formatDayMonth } from '../../utils/dateFormat';
import { useAccess } from '../../hooks/useAccess';
import { useFathomIngests, useFathomSyncStatus, useSuggestions, useSyncFathom } from '../../hooks/useOrganizer';

/**
 * How far back a manual check looks.
 *
 * The scheduled poll uses 4 hours, which is right for a every-30-minutes cron but
 * useless for a first connect — it finds nothing and looks broken. So a manual
 * check asks, and defaults to a week.
 */
const LOOKBACK_CHOICES = [
  { hours: 24, label: '24 hours' },
  { hours: 24 * 7, label: '7 days' },
  { hours: 24 * 30, label: '30 days' },
] as const;

const SKIP_EXPLANATIONS: Record<NonNullable<FathomIngest['skipReason']>, string> = {
  no_transcript: 'no transcript available',
  too_short: 'transcript too short to contain a commitment',
  internal_only: 'internal-only meeting',
  no_tasks: 'nothing you committed to',
  filtered_out: 'filtered out',
};

/** `waiting` = this call's to-dos still in Suggested; undefined while unknown. */
function stateLabel(ingest: FathomIngest, waiting: number | undefined): string {
  if (ingest.state === 'extracted') return callCountLabel(ingest.suggestionsCreated, waiting);
  if (ingest.state === 'skipped') {
    return ingest.skipReason ? SKIP_EXPLANATIONS[ingest.skipReason] : 'skipped';
  }
  if (ingest.state === 'failed') return 'failed';
  return ingest.state;
}

/**
 * The verdict on a check the user just pressed.
 *
 * A check that scanned nothing is the case worth spelling out: it is the one
 * that otherwise leaves the list empty and looks identical to a broken
 * integration, so the server's reason is shown verbatim rather than summarised.
 * Whether that reason reads as a warning or as a plain note is the server's
 * call: a quiet window is not a problem, and dressing it as one would leave the
 * warning styling meaning nothing.
 */
function SyncOutcome({
  status,
  statusError,
  onDismiss,
}: {
  status: FathomSyncStatus | undefined;
  statusError: unknown;
  onDismiss: () => void;
}) {
  // The check may well be running fine; what failed is our ability to read it.
  // Saying so beats an animation that would otherwise never resolve.
  if (statusError) {
    return (
      <Alert variant="warning" dismissible onDismiss={onDismiss}>
        Couldn't read how the check ended —{' '}
        {statusError instanceof Error ? statusError.message : 'the status request failed'}. The
        check itself may still have run; see the calls listed below.
      </Alert>
    );
  }

  if (!status || status.status === 'running') {
    return (
      <Alert variant="info">
        Checking Fathom… this can take a minute or two over a wide window.
      </Alert>
    );
  }

  if (status.status === 'error') {
    return (
      <Alert variant="error" dismissible onDismiss={onDismiss}>
        {status.error}
      </Alert>
    );
  }

  // The job was archived before we polled it. The check itself may well have
  // worked, so the call list below is the honest source of truth.
  if (status.status === 'not_found') {
    return (
      <Alert variant="info" dismissible onDismiss={onDismiss}>
        The check finished a while ago — see the calls listed below.
      </Alert>
    );
  }

  const { meetingsScanned, suggestionsCreated, reason, reasonActionable } = status.result;

  if (reason) {
    return (
      <Alert variant={reasonActionable ? 'warning' : 'info'} dismissible onDismiss={onDismiss}>
        {reason}
      </Alert>
    );
  }

  return (
    <Alert variant="success" dismissible onDismiss={onDismiss}>
      Checked {meetingsScanned} {meetingsScanned === 1 ? 'call' : 'calls'} —{' '}
      {suggestionsCreated === 0
        ? 'nothing you committed to'
        : `${suggestionsCreated} to-do${suggestionsCreated === 1 ? '' : 's'} for review`}
      .
    </Alert>
  );
}

/**
 * Which calls have been looked at, and what came of each.
 *
 * This is the answer to "the inbox is empty — is it working?". Without it, a
 * broken pipeline and a quiet week are indistinguishable, so a skipped call is
 * shown with its reason rather than omitted.
 */
export function FathomSyncPanel() {
  const { isAdmin } = useAccess();
  const { data, isError, error } = useFathomIngests();
  const sync = useSyncFathom();
  const [failure, setFailure] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [queuedUnfollowable, setQueuedUnfollowable] = useState(false);
  const [lookbackHours, setLookbackHours] = useState<number>(24 * 7);
  const { data: syncStatus, error: syncStatusError } = useFathomSyncStatus(jobId);

  const ingests = data?.ingests ?? [];
  const totalCalls = data?.total;
  // Same cache as the Suggested list, so "waiting" here is the count shown there.
  const suggestionsQuery = useSuggestions();
  const waiting = suggestionsQuery.data?.suggestions.length;
  const waitingByCall = useMemo(() => {
    if (!suggestionsQuery.data) return null;
    const map = new Map<string, number>();
    for (const s of suggestionsQuery.data.suggestions) {
      if (s.source === 'fathom') map.set(s.sourceRef, (map.get(s.sourceRef) ?? 0) + 1);
    }
    return map;
  }, [suggestionsQuery.data]);
  const extracted = ingests.reduce((sum, i) => sum + (i.state === 'extracted' ? i.suggestionsCreated : 0), 0);

  return (
    <div className="space-y-3 rounded-xl border border-border-light bg-surface-secondary p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-body-sm font-medium text-text-primary">Calls checked</h3>
          <p className="text-body-xs text-text-tertiary">
            Fathom is polled every 30 minutes. The latest calls are listed with what came of each.
          </p>
          {ingests.length > 0 && (
            <p
              className="mt-0.5 text-body-xs text-text-secondary"
              title="Waiting is what is still in Suggested, across every call. Found counts the to-dos found in the calls listed below only; the rest were accepted or rejected."
            >
              {suggestionsQuery.isError ? 'Waiting count unavailable' : waiting === undefined ? '…' : `${waiting} waiting`} · {extracted} found in the{' '}
              {totalCalls !== undefined && totalCalls > ingests.length
                ? `latest ${ingests.length} of ${totalCalls} calls`
                : ingests.length === 1
                  ? 'latest call'
                  : `latest ${ingests.length} calls`}
            </p>
          )}
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <label htmlFor="fathom-check-window" className="text-body-xs text-text-tertiary">
              Check window
            </label>
            <Select
              id="fathom-check-window"
              size="sm"
              value={lookbackHours}
              onChange={(e) => setLookbackHours(Number(e.target.value))}
              title="How far back Check now looks. It doesn't filter the list below."
            >
              {LOOKBACK_CHOICES.map((choice) => (
                <option key={choice.hours} value={choice.hours}>
                  {choice.label}
                </option>
              ))}
            </Select>
            <Button
              size="sm"
              variant="outline"
              isLoading={sync.isPending}
              onClick={() => {
                setFailure(null);
                setJobId(null);
                setQueuedUnfollowable(false);
                sync.mutate(lookbackHours, {
                  // The queue can accept a job without handing back an id, and a
                  // press that produces no visible answer at all is the one
                  // outcome this panel exists to rule out.
                  onSuccess: (res) => {
                    if (res.jobId) setJobId(res.jobId);
                    else setQueuedUnfollowable(true);
                  },
                  onError: (err) => {
                    console.error('[Organizer] Fathom sync failed to queue', err);
                    setFailure(err instanceof Error ? err.message : String(err));
                  },
                });
              }}
            >
              Check now
            </Button>
          </div>
        )}
      </div>

      {failure && (
        <Alert variant="error" dismissible onDismiss={() => setFailure(null)}>
          Couldn't start a Fathom check — {failure}
        </Alert>
      )}

      {queuedUnfollowable && !failure && (
        <Alert variant="info" dismissible onDismiss={() => setQueuedUnfollowable(false)}>
          The check was queued, but the queue returned no id to follow it by — its result will show
          up in the calls listed below once it has run.
        </Alert>
      )}

      {jobId && !failure && (
        <SyncOutcome
          status={syncStatus}
          statusError={syncStatusError}
          onDismiss={() => setJobId(null)}
        />
      )}

      {isError && (
        <Alert variant="warning">
          Couldn't load the call history — {error instanceof Error ? error.message : 'unknown error'}
        </Alert>
      )}

      {!isError && ingests.length === 0 && (
        <p className="text-body-xs text-text-muted">
          No calls checked yet. The scheduled poll only looks back four hours, so existing
          recordings never appear on their own — run a check over 30 days to pick them up. If that
          finds nothing it will say why, rather than leaving this empty.
        </p>
      )}

      {ingests.length > 0 && (
        <ul className="divide-y divide-border-light">
          {ingests.map((ingest) => (
            <li key={ingest.id} className="flex items-baseline justify-between gap-3 py-2">
              <span className="w-12 shrink-0 text-body-xs tabular-nums text-text-tertiary">
                {formatDayMonth(ingest.recordedAt ?? ingest.createdAt)}
              </span>
              <span className="min-w-0 flex-1 truncate text-body-xs text-text-secondary">
                {ingest.meetingTitle ?? `Recording ${ingest.fathomRecordingId}`}
              </span>
              <span
                className={clsx(
                  'shrink-0 text-body-xs',
                  ingest.state === 'failed'
                    ? 'text-error'
                    : ingest.state === 'extracted'
                      ? 'text-accent-dark font-medium'
                      : 'text-text-muted',
                )}
                title={ingest.error ?? undefined}
              >
                {stateLabel(ingest, waitingByCall ? (waitingByCall.get(String(ingest.fathomRecordingId)) ?? 0) : undefined)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
